import axios from 'axios';
import { getCookie } from 'helpers/saveToCookies';
import { getChannelContextAdditionalInfo } from 'helpers/Utility';
import { refreshPartnerToken } from 'helpers/refreshPartnerToken';
import { BASE_URL_GOLANG } from 'common/config';

const PARTNER_TOKEN_INVALID_CODE = ['555008', '555005', '555007', '555016'];
const USER_TOKEN_INVALID_CODE = ['555014', '555021'];

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

export const createAxiosInstance = (baseURL) => {
  const instance = axios.create({
    baseURL,
    timeout: 90000,
    headers: {
      'go-auth': 'true' 
    }
  });

  // Request Interceptor — inject token
  instance.interceptors.request.use(
    (config) => {
      const token = getCookie('PARTNER_TOKEN');
      if (token) config.headers['partner-auth-token'] = token;
      return config;
    },
    (error) => Promise.reject(error)
  );

  // Response Interceptor — handle token expiry
  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;
      const errorCode = getChannelContextAdditionalInfo(error, 'channel-context');

      if (USER_TOKEN_INVALID_CODE.includes(errorCode)) {
        window.location.href = '/';
        return Promise.reject(error);
      }

      if (PARTNER_TOKEN_INVALID_CODE.includes(errorCode) && !originalRequest._retry) {
        originalRequest._retry = true;

        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          }).then((newToken) => {
            originalRequest.headers['partner-auth-token'] = newToken;
            return instance(originalRequest);
          });
        }

        isRefreshing = true;

        try {
          const newToken = await refreshPartnerToken(BASE_URL_GOLANG);
          instance.defaults.headers.common['partner-auth-token'] = newToken;
          processQueue(null, newToken);
          originalRequest.headers['partner-auth-token'] = newToken;
          return instance(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError);
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      return Promise.reject(error);
    }
  );

  return instance;
};
